import {
  Component,
  OnDestroy,
  OnInit,
  Output,
  EventEmitter,
  signal,
  computed,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgSelectModule } from '@ng-select/ng-select';
import Quill from 'quill';
import SnowTheme from 'quill/themes/snow';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { Spinner } from '@app/shared/components/spinner/spinner';
import { EditorRichText } from '@app/shared/components/editor-rich-text/editor-rich-text';
import { ApiService } from '@app/core/services/API/api-service';
import {
  NavigationService,
  NavigationItem,
  NavigationSubItem,
} from '@app/core/services/sidebar/sidebar.service';

// Register Quill Snow theme for Quill 2.x (same as request form)
Quill.register('themes/snow', SnowTheme, true);

export type FeedbackType = 'issue' | 'suggestion';

export interface FeedbackPayload {
  type: FeedbackType;
  subject: string;
  url: string;
  description: string;
  pageMenu: string;
  pageSubMenu: string;
  pageMenuLabel: string;
  pageSubMenuLabel: string;
  files: File[];
}

@Component({
  selector: 'app-feedback-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    NgSelectModule,
    EditorRichText,
    Spinner,
  ],
  templateUrl: './feedback-form.html',
  styleUrl: './feedback-form.scss',
  encapsulation: ViewEncapsulation.None,
})
export class FeedbackForm implements OnInit, OnDestroy {
  @Output() close = new EventEmitter<void>();
  @Output() feedbackSubmitted = new EventEmitter<FeedbackPayload>();

  feedbackForm!: FormGroup;
  isSubmitting = signal(false);
  selectedFiles = signal<File[]>([]);
  pageMenus = signal<NavigationItem[]>([]);
  selectedPageMenu = signal<string | null>(null);
  isDragOver = signal(false);

  readonly typeOptions: { label: string; value: FeedbackType }[] = [
    { label: 'Issue', value: 'issue' },
    { label: 'Suggestion', value: 'suggestion' },
  ];

  private readonly destroy$ = new Subject<void>();
  private readonly maxFileSizeBytes = 25 * 1024 * 1024;
  private readonly acceptedMimePrefix = ['image/', 'video/'];
  private readonly previewUrls = new Map<string, string>();

  readonly subMenus = computed(() => {
    const selected = this.selectedPageMenu();
    if (!selected) {
      return [] as NavigationSubItem[];
    }
    const menu = this.pageMenus().find(
      (item) => item.name === selected || item.to === selected || item.label === selected
    );
    return menu?.subs ?? [];
  });

  readonly hasSubMenus = computed(() => this.subMenus().length > 0);

  constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly navigationService: NavigationService,
    private readonly toastr: ToastrService,
    private readonly apiService: ApiService
  ) {}

  ngOnInit(): void {
    this.feedbackForm = this.fb.group({
      type: ['issue' as FeedbackType, Validators.required],
      subject: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(200)]],
      url: ['', [this.optionalUrlValidator]],
      description: ['', [this.richTextLengthValidator({ max: 2000 })]],
      pageMenu: [null as string | null],
      pageSubMenu: [null as string | null],
    });

    this.navigationService
      .getNavigationConfig()
      .pipe(takeUntil(this.destroy$))
      .subscribe((items) => {
        this.pageMenus.set(items ?? []);
        this.prefillActivePage();
      });

    this.feedbackForm
      .get('pageMenu')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value: string | null) => {
        this.selectedPageMenu.set(value);
        this.feedbackForm.get('pageSubMenu')?.setValue(null);
      });
  }

  ngOnDestroy(): void {
    this.previewUrls.forEach((url) => URL.revokeObjectURL(url));
    this.previewUrls.clear();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getCurrentPageUrl(): string {
    if (typeof window !== 'undefined' && window.location?.href) {
      return window.location.href;
    }
    return this.router.url;
  }

  private prefillActivePage(): void {
    if (this.feedbackForm.get('pageMenu')?.value) {
      return;
    }

    this.navigationService
      .getActiveHeader()
      .pipe(takeUntil(this.destroy$))
      .subscribe((active) => {
        if (!active || this.feedbackForm.get('pageMenu')?.value) {
          return;
        }
        const menuKey = active.name || active.label;
        this.selectedPageMenu.set(menuKey);
        this.feedbackForm.patchValue({ pageMenu: menuKey }, { emitEvent: false });

        this.navigationService
          .getActiveSubItem()
          .pipe(takeUntil(this.destroy$))
          .subscribe((sub) => {
            if (!sub || this.feedbackForm.get('pageSubMenu')?.value) {
              return;
            }
            if (this.hasSubMenus()) {
              this.feedbackForm.patchValue({
                pageSubMenu: sub.to || sub.objCode || sub.label,
              });
            }
          });
      });
  }

  private richTextLengthValidator(config: {
    required?: boolean;
    min?: number;
    max?: number;
  }) {
    return (control: AbstractControl): ValidationErrors | null => {
      const text = this.extractPlainText(control.value);
      if (config.required && !text.length) {
        return { required: true };
      }
      if (!text.length) {
        return null;
      }
      if (config.min && text.length < config.min) {
        return {
          minlength: { requiredLength: config.min, actualLength: text.length },
        };
      }
      if (config.max && text.length > config.max) {
        return {
          maxlength: { requiredLength: config.max, actualLength: text.length },
        };
      }
      return null;
    };
  }

  private extractPlainText(value: unknown): string {
    const html = String(value ?? '');
    if (!html) {
      return '';
    }
    if (typeof document !== 'undefined') {
      const div = document.createElement('div');
      div.innerHTML = html;
      return (div.textContent || div.innerText || '')
        .replace(/\u00a0/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }
    return html
      .replace(/<[^>]*>/g, ' ')
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private optionalUrlValidator(control: AbstractControl): ValidationErrors | null {
    const value = String(control.value ?? '').trim();
    if (!value) {
      return null;
    }

    try {
      const normalizedValue = /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`;
      const parsedUrl = new URL(normalizedValue);
      const isHttpUrl = parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
      const hasHostname = !!parsedUrl.hostname && parsedUrl.hostname.includes('.');
      return isHttpUrl && hasHostname ? null : { invalidUrl: true };
    } catch {
      return { invalidUrl: true };
    }
  }

  isFieldInvalid(controlName: string): boolean {
    const control = this.feedbackForm.get(controlName);
    return !!(control && control.invalid && (control.touched || control.dirty));
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) {
      return;
    }
    this.addFiles(Array.from(input.files));
    input.value = '';
  }

  /** Toolbar upload from the rich-text editor (local pick — no document objID). */
  onEditorFilesSelected(files: File[]): void {
    if (!files?.length) {
      return;
    }
    this.addFiles(files);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);
    const files = event.dataTransfer?.files;
    if (files?.length) {
      this.addFiles(Array.from(files));
    }
  }

  private addFiles(files: File[]): void {
    let accepted: File | null = null;
    for (const file of files) {
      const isMedia = this.acceptedMimePrefix.some((prefix) =>
        (file.type || '').startsWith(prefix)
      );
      if (!isMedia) {
        this.toastr.warning(`"${file.name}" is not an image or video.`);
        continue;
      }
      if (file.size > this.maxFileSizeBytes) {
        this.toastr.warning(`"${file.name}" exceeds the 25MB limit.`);
        continue;
      }
      accepted = file;
      break;
    }

    if (!accepted) {
      return;
    }

    if (files.length > 1) {
      this.toastr.info('Only one file can be uploaded. Using the first valid file.');
    }

    this.previewUrls.forEach((url) => URL.revokeObjectURL(url));
    this.previewUrls.clear();
    this.selectedFiles.set([accepted]);
  }

  removeFile(file: File): void {
    const key = `${file.name}-${file.size}`;
    const preview = this.previewUrls.get(key);
    if (preview) {
      URL.revokeObjectURL(preview);
      this.previewUrls.delete(key);
    }
    this.selectedFiles.set(this.selectedFiles().filter((f) => f !== file));
  }

  isImageFile(file: File): boolean {
    return (file.type || '').startsWith('image/');
  }

  isVideoFile(file: File): boolean {
    return (file.type || '').startsWith('video/');
  }

  getImagePreview(file: File): string {
    const key = `${file.name}-${file.size}`;
    const existing = this.previewUrls.get(key);
    if (existing) {
      return existing;
    }
    const url = URL.createObjectURL(file);
    this.previewUrls.set(key, url);
    return url;
  }

  getFileSize(file: File): string {
    const bytes = file.size;
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  onCancel(): void {
    this.close.emit();
  }

  onSubmit(): void {
    if (this.feedbackForm.invalid) {
      this.feedbackForm.markAllAsTouched();
      this.toastr.warning('Please fill in all required fields.');
      return;
    }

    if (this.isSubmitting()) {
      return;
    }

    const value = this.feedbackForm.getRawValue();
    const menus = this.pageMenus();
    const selectedMenu = menus.find(
      (item) =>
        item.name === value.pageMenu ||
        item.to === value.pageMenu ||
        item.label === value.pageMenu
    );
    const selectedSub = (selectedMenu?.subs ?? []).find(
      (sub) =>
        sub.to === value.pageSubMenu ||
        sub.objCode === value.pageSubMenu ||
        sub.label === value.pageSubMenu
    );

    const payload: FeedbackPayload = {
      type: value.type,
      subject: value.subject.trim(),
      url: (value.url ?? '').trim(),
      // Persist Quill HTML so links/lists/toolbar formats survive (plain text only for validators).
      description: String(value.description ?? ''),
      pageMenu: value.pageMenu ?? '',
      pageSubMenu: value.pageSubMenu ?? '',
      pageMenuLabel: selectedMenu?.label ?? value.pageMenu ?? '',
      pageSubMenuLabel: selectedSub?.label ?? value.pageSubMenu ?? '',
      files: this.selectedFiles(),
    };

    const formData = this.buildFeedbackFormData(payload);

    this.isSubmitting.set(true);
    this.apiService
      .submitFeedback(formData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.feedbackSubmitted.emit(payload);
          this.toastr.success('Thank you! Your feedback has been submitted.');
          this.isSubmitting.set(false);
          this.close.emit();
        },
        error: (err) => {
          console.error('Feedback submission failed:', err);
          this.toastr.error(
            err?.error?.message || 'Failed to submit feedback. Please try again.'
          );
          this.isSubmitting.set(false);
        },
      });
  }

  private buildFeedbackFormData(payload: FeedbackPayload): FormData {
    const formData = new FormData();
    formData.append('subject', payload.subject);
    formData.append('description', payload.description);
    formData.append('type', payload.type);
    if (payload.pageMenu) {
      formData.append('pageMenu', payload.pageMenuLabel || payload.pageMenu);
    }
    if (payload.url) {
      formData.append('url', payload.url);
    }

    if (payload.pageSubMenu) {
      formData.append(
        'pageSubMenu',
        payload.pageSubMenuLabel || payload.pageSubMenu
      );
    }

    payload.files.forEach((file) => {
      formData.append('document', file, file.name);
    });

    return formData;
  }
}
